import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';

import { ApiClientService } from '../../../core/http/api-client.service';
import {
  CreateProcedureTypeRequest,
  ProcedureTypeListSort,
  ProcedureTypeAssignedClinicDto,
  ProcedureTypeAssignedClinicItem,
  ProcedureTypeDetailDto,
  ProcedureTypeDetailItem,
  ProcedureTypeDto,
  ProcedureTypeItem,
  ProcedureTypePage,
  ProcedureTypePageDto,
  ProcedureTypeStatusFilter,
  UpdateProcedureTypeRequest,
} from './procedure-types.models';

@Injectable({
  providedIn: 'root'
})
export class ProcedureTypesService
{
  private readonly apiClient = inject(ApiClientService);

  public getList(params?: { includeInactive?: boolean; clinicId?: string; companyId?: string }): Observable<ProcedureTypeItem[]>
  {
    const query: Record<string, string> = {};
    if (params?.includeInactive) {
      query['includeInactive'] = 'true';
    }
    if (params?.clinicId) {
      query['clinicId'] = params.clinicId;
    }
    if (params?.companyId) {
      query['companyId'] = params.companyId;
    }

    return this.apiClient.get<ProcedureTypeDto[] | null>(
      '/procedure-types',
      query,
    ).pipe(
      map((items: ProcedureTypeDto[] | null) =>
      {
        if (!Array.isArray(items))
        {
          return [];
        }

        return items.map((item) =>
        ({
          id: item.id,
          name: item.name,
          deviceTypeId: item.deviceTypeId,
          defaultDurationMinutes: item.defaultDurationMinutes,
          isActive: item.isActive ?? true,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
        }));
      })
    );
  }

  public getPage(params: {
    companyId?: string;
    limit: number;
    cursor?: string | null;
    query?: string;
    status?: ProcedureTypeStatusFilter;
    deviceTypeId?: string;
    sort?: ProcedureTypeListSort;
  }): Observable<ProcedureTypePage> {
    return this.apiClient.get<ProcedureTypePageDto>(
      '/procedure-types',
      {
        ...(params.companyId ? { companyId: params.companyId } : {}),
        limit: String(params.limit),
        cursor: params.cursor ?? undefined,
        q: params.query?.trim() || undefined,
        status: params.status || undefined,
        deviceTypeId: params.deviceTypeId || undefined,
        sort: params.sort ?? 'createdAtDesc',
      },
    ).pipe(
      map((page) => ({
        items: Array.isArray(page?.items) ? page.items.map((item) => this.map(item)) : [],
        nextCursor: page?.nextCursor ?? null,
        hasMore: !!page?.hasMore,
      })),
    );
  }

  public create(payload: CreateProcedureTypeRequest): Observable<ProcedureTypeItem>
  {
    const normalizedPayload: CreateProcedureTypeRequest = {
      ...payload,
      deviceTypeId: payload.deviceTypeId?.trim() ? payload.deviceTypeId.trim() : undefined,
    };

    return this.apiClient.post<ProcedureTypeDto, CreateProcedureTypeRequest>(
      '/procedure-types',
      normalizedPayload,
    ).pipe(map((item) => this.map(item)));
  }

  public getById(id: string, companyId?: string): Observable<ProcedureTypeItem>
  {
    const normalizedId = id.trim();
    return this.apiClient.get<ProcedureTypeDto>(
      `/procedure-types/${normalizedId}`,
      companyId ? { companyId } : undefined,
    ).pipe(map((item) => this.map(item)));
  }

  public getDetailById(id: string, companyId?: string): Observable<ProcedureTypeDetailItem>
  {
    const normalizedId = id.trim();
    return this.apiClient.get<ProcedureTypeDetailDto>(
      `/procedure-types/${encodeURIComponent(normalizedId)}/detail`,
      companyId ? { companyId } : undefined,
    ).pipe(
      map((item) => {
        const hasAssignedClinics = Array.isArray(item.assignedClinics);

        return {
          ...this.map(item),
          deviceTypeName: item.deviceTypeName ?? null,
          assignedClinics: hasAssignedClinics
            ? item.assignedClinics!.map((clinic) => this.mapAssignedClinic(clinic))
            : [],
          showStatus: typeof item.isActive === 'boolean',
          showAssignedClinics: hasAssignedClinics,
        };
      }),
    );
  }

  public update(id: string, payload: UpdateProcedureTypeRequest): Observable<ProcedureTypeItem>
  {
    const normalizedPayload: UpdateProcedureTypeRequest = {
      ...payload,
      deviceTypeId:
        payload.deviceTypeId === null
          ? null
          : (payload.deviceTypeId?.trim() ? payload.deviceTypeId.trim() : undefined),
    };

    return this.apiClient.patch<ProcedureTypeDto, UpdateProcedureTypeRequest>(
      `/procedure-types/${id}`,
      normalizedPayload,
    ).pipe(map((item) => this.map(item)));
  }

  private map(item: ProcedureTypeDto): ProcedureTypeItem {
    return {
      id: item.id,
      name: item.name,
      deviceTypeId: item.deviceTypeId,
      defaultDurationMinutes: item.defaultDurationMinutes,
      isActive: item.isActive ?? true,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  private mapAssignedClinic(
    item: ProcedureTypeAssignedClinicDto,
  ): ProcedureTypeAssignedClinicItem {
    return {
      id: item.id,
      name: item.name,
      city: item.city,
      address: item.address,
    };
  }
}
